import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Dropdown from '../../Assets/Dropdown';
import { nbaTeams, nbaStats, higherIsBetter } from '../../../utils/nbaUtils';
import { UpCaret, DownCaret } from '../../common/icons';

const NbaStatAvgs = (props) => {
    const game = props.game;
    const home_team = nbaTeams[game.home.team_id];
    const away_team = nbaTeams[game.away.team_id];
    const showCarets = props.showCarets || false;

    const [offStats, setOffStats] = useState(null);
    const [defStats, setDefStats] = useState(null);
    const [genStats, setGenStats] = useState(null);
    const [activeTab, setActiveTab] = useState('off');

    // Define color constants
    const BETTER_COLOR = "#43a047"; // Green
    const WORSE_COLOR = "#e53935"; // Red
    
    const offLabels = {
        "stats.Possessions": "Possessions",
        "stats.FieldGoalsMade": "FG Made",
        "stats.FieldGoalsAttempted": "FG Attempted",
        "calculated.FieldGoalPercentage": "FG Percentage",
        "stats.TwoPointersMade": "2pt Made",
        "stats.TwoPointersAttempted": "2pt Attempts",
        "stats.ThreePointersMade": "3pt Made",
        "stats.ThreePointersAttempted": "3pt Attempts",
        "stats.FreeThrowsMade": "FT Made",
        "stats.OffensiveRebounds": "Off Rebounds",
        "stats.Assists": "Assists",
        "stats.Turnovers": "Turnovers",
        "stats.Points": "Pts Scored",
        "stats.TrueShootingAttempts": "True Shooting Attempts",
        "calculated.TrueShootingPercentage": "True Shooting Percentage",
        "stats.DoubleDoubles": "Double Doubles",
        "stats.TripleDoubles": "Triple Doubles",
        "calculated.TwoPtFgPercentage": "2pt FG Percentage",
        "calculated.ThreePtFgPercentage": "3pt FG Percentage",
        "calculated.FtPercentage": "FT Percentage",
        "calculated.EffectiveFgPercentage": "Effective FG Percentage",
        "calculated.PtsPerPossession": "Pts per Possession",
        "calculated.FtRate": "FT Rate",
        "calculated.ThreePtAttemptRate": "3pt Attempt Rate",
        "calculated.PercentagePtsFrom2": "% of Pts from Two",
        "calculated.PercentagePtsFrom3": "% of Pts from Three",
        "calculated.PercentagePtsFromFt": "% of Pts from FT",
        "calculated.OffensiveEfficiencyRating": "Off Efficiency Rating",
        "calculated.AssistPercentage": "Assist Percentage",
        "calculated.AssistToTurnoverRatio": "Assist to Turnover Ratio",
        "calculated.TurnoverRate": "Turnover Rate",
        "calculated.TurnoverPercentage": "Turnover Percentage"
    };
    
    const defLabels = {
        "stats.DefensiveRebounds": "Def Rebounds",
        "stats.Steals": "Steals",
        "stats.BlockedShots": "Blocks",
        "calculated.BlockToFoulRatio": "Block to Foul Ratio"
    }

    const genLabels = {
        "stats.Rebounds": "Total Rebounds",
        "stats.PersonalFouls": "Personal Fouls",
    };

    // Determine if home or away has better stat
    const compareStats = useCallback((stat, homeVal, awayVal) => {
        if (homeVal === awayVal) return null;
        
        // Use the imported higherIsBetter object
        const isHigherBetter = higherIsBetter[stat];
        
        if (isHigherBetter) {
            return homeVal > awayVal ? 'home' : 'away';
        } else {
            return homeVal < awayVal ? 'home' : 'away';
        }
    }, []);

    // Memoize renderTableRows to avoid infinite loops
    const renderTableRows = useCallback((stats, labels) => {
        if (!stats || Object.keys(stats).length === 0) {
            return (
                <tr>
                    <td colSpan="3" className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">Stats Unavailable</td>
                </tr>
            );
        }

        return Object.keys(stats).map((stat, idx) => {
            const label = labels[stat] || "";
            const homeValue = stats[stat][0];
            const awayValue = stats[stat][1];
            const better = showCarets ? compareStats(stat, homeValue, awayValue) : null;
            const isHigherBetter = higherIsBetter[stat];
            const isLast = idx === Object.keys(stats).length - 1;
            
            return (
                <tr key={stat}>
                    <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} truncate py-4 pl-6 pr-3 text-sm font-medium text-gray-900 dark:text-gray-100 sm:pl-8 lg:pl-10`}>
                        {label}
                    </td>
                    <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} truncate px-3 py-4 text-sm text-gray-900 dark:text-gray-100 ${better === 'home' ? 'font-medium text-green-700 dark:text-green-500' : better === 'away' ? 'font-medium text-red-700 dark:text-red-500' : ''}`}>
                        {homeValue}
                        {better === 'home' && (
                            isHigherBetter ? (
                                <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                            ) : (
                                <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                            )
                        )}
                        {better === 'away' && (
                            isHigherBetter ? (
                                <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                            ) : (
                                <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                            )
                        )}
                    </td>
                    <td className={`${!isLast ? 'border-b border-gray-200 dark:border-gray-700' : ''} truncate px-3 py-4 text-sm text-gray-900 dark:text-gray-100 ${better === 'away' ? 'font-medium text-green-700 dark:text-green-500' : better === 'home' ? 'font-medium text-red-700 dark:text-red-500' : ''}`}>
                        {awayValue}
                        {better === 'away' && (
                            isHigherBetter ? (
                                <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                            ) : (
                                <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={BETTER_COLOR} />
                            )
                        )}
                        {better === 'home' && (
                            isHigherBetter ? (
                                <DownCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                            ) : (
                                <UpCaret className="inline-block ml-1 w-4 h-4" fillColor={WORSE_COLOR} />
                            )
                        )}
                    </td>
                </tr>
            );
        });
    }, [showCarets, compareStats, BETTER_COLOR, WORSE_COLOR]);

    // Run when props change - get the stats arrays
    useEffect(()=>{
        if('stats' in game.home && 'stats' in game.away){
            const offKeys = Object.keys(nbaStats.off);
            const defKeys = Object.keys(nbaStats.def);
            const genKeys = Object.keys(nbaStats.gen);

            let tmpOffStats = {}, tmpDefStats = {}, tmpGenStats = {};

            // get offensive stats
            offKeys.forEach(stat=>{
                // get stat split (stat/calc, and stat)
                const statSplit = stat.split(".");
                // add stat
                tmpOffStats[stat] = [game.home[statSplit[0]][statSplit[1]], game.away[statSplit[0]][statSplit[1]]]
            });
            // get offensive passing stats
            defKeys.forEach(stat=>{
                // get stat split (stat/calc, and stat)
                const statSplit = stat.split(".");
                // add stat
                tmpDefStats[stat] = [game.home[statSplit[0]][statSplit[1]], game.away[statSplit[0]][statSplit[1]]]
            });
            genKeys.forEach(stat=>{
                // get stat split (stat/calc, and stat)
                const statSplit = stat.split(".");
                // add stat
                tmpGenStats[stat] = [game.home[statSplit[0]][statSplit[1]], game.away[statSplit[0]][statSplit[1]]]
            });

            // set stats state variables
            setOffStats(tmpOffStats);
            setDefStats(tmpDefStats);
            setGenStats(tmpGenStats);
        };
    }, [game]);

    // Memoize the rendered rows
    const renderedRows = useMemo(() => {
        if (activeTab === 'off' && offStats) {
            return renderTableRows(offStats, offLabels);
        } else if (activeTab === 'def' && defStats) {
            return renderTableRows(defStats, defLabels);
        } else if (activeTab === 'gen' && genStats) {
            return renderTableRows(genStats, genLabels);
        }
        return null;
    }, [activeTab, offStats, defStats, genStats, renderTableRows, offLabels, defLabels, genLabels]);
    
    return (
        <div className="sm:max-w-[600px] md:max-w-[700px] lg:max-w-[800px] px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-4 relative z-20 mt-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Stat Averages</h3>
                <Dropdown
                    id="stat-type-select"
                    title="Select Stats Type"
                    items={[
                        { value: 'off', label: 'Offense' },
                        { value: 'def', label: 'Defense' },
                        { value: 'gen', label: 'General' }
                    ]}
                    activeItem={activeTab}
                    onSelect={setActiveTab}
                    size="md"
                    variant="default"
                />
            </div>
            
            <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
                    <div className="inline-block w-full py-2 align-middle">
                        <div className="max-h-[600px] overflow-y-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg dark:ring-gray-700">
                            <table className="min-w-full w-full table-fixed border-separate border-spacing-0">
                                <thead>
                                    <tr>
                                        <th scope="col" className="w-[50%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 py-3.5 pl-6 pr-3 text-left text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter sm:pl-8 lg:pl-10 dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">Stat</th>
                                        <th scope="col" className="w-[25%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-4 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">{home_team}</th>
                                        <th scope="col" className="w-[25%] sticky top-0 z-10 border-b border-gray-300 bg-white/75 px-4 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur-sm backdrop-filter dark:bg-gray-800/75 dark:border-gray-700 dark:text-gray-100">{away_team}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {renderedRows}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NbaStatAvgs;